import { gql, useQuery } from '@apollo/client'
import { Trans } from '@lingui/macro'
import { useWeb3React } from '@web3-react/core'
import { SupportedChainId } from 'constants/chains'
import useBlockNumber from 'lib/hooks/useBlockNumber'
import { useEffect, useMemo, useState } from 'react'
import { RefreshCw } from 'react-feather'
import { Link } from 'react-router-dom'
import styled from 'styled-components/macro'
import { ThemedText } from 'theme'
import { formatTokenQuantity } from 'utils/formatNumbers'

import {
  HARMONY_V2_SUBGRAPH_URL,
  HARMONY_V3_SUBGRAPH_URL,
  v2AnalyticsApolloClient,
  v3AnalyticsApolloClient,
} from '../../graphql/thegraph/apollo'

const V2_FACTORY_ADDRESS = '0x0Dea90EC11032615E027664D2708BC292Bbd976B'

const V2_ANALYTICS_QUERY = gql`
  query V2AnalyticsOverview($transactionSkip: Int!, $chartStart: BigInt!, $activityStart: BigInt!) {
    _meta {
      block {
        number
      }
      hasIndexingErrors
    }
    uniswapFactories(where: { id: "${V2_FACTORY_ADDRESS}" }) {
      id
      pairCount
      txCount
      totalVolumeUSD
      totalLiquidityUSD
      totalLiquidityETH
    }
    bundle(id: "1") {
      ethPrice
    }
    pairs(first: 100, orderBy: trackedReserveETH, orderDirection: desc) {
      id
      txCount
      reserve0
      reserve1
      reserveUSD
      volumeUSD
      untrackedVolumeUSD
      token0 {
        id
        symbol
        name
        derivedETH
      }
      token1 {
        id
        symbol
        name
        derivedETH
      }
    }
    tokens(first: 100, orderBy: tradeVolumeUSD, orderDirection: desc) {
      id
      symbol
      name
      txCount
      tradeVolumeUSD
      totalLiquidity
      derivedETH
    }
    uniswapDayDatas(first: 301, orderBy: date, orderDirection: desc) {
      id
      date
      dailyVolumeUSD
      totalLiquidityUSD
      txCount
    }
    transactions(first: 20, skip: $transactionSkip, orderBy: timestamp, orderDirection: desc) {
      id
      timestamp
      swaps {
        amountUSD
        amount0In
        amount0Out
        amount1In
        amount1Out
        to
        pair {
          token0 {
            symbol
            derivedETH
          }
          token1 {
            symbol
            derivedETH
          }
        }
      }
    }
    recentTransactions: transactions(
      first: 1000
      orderBy: timestamp
      orderDirection: desc
      where: { timestamp_gte: $activityStart }
    ) {
      id
      timestamp
    }
    chartTransactions: transactions(first: 1000, orderBy: timestamp, orderDirection: desc, where: { timestamp_gte: $chartStart }) {
      id
      timestamp
      swaps {
        amountUSD
        amount0In
        amount0Out
        amount1In
        amount1Out
        to
        pair {
          token0 { symbol derivedETH }
          token1 { symbol derivedETH }
        }
      }
    }
  }
`

const V3_ANALYTICS_QUERY = gql`
  query V3AnalyticsOverview($swapSkip: Int!, $chartStart: BigInt!, $activityStart: BigInt!) {
    _meta {
      block {
        number
      }
      hasIndexingErrors
    }
    factories(first: 1) {
      id
      poolCount
      txCount
      totalVolumeUSD
      totalFeesUSD
      totalValueLockedUSD
    }
    pools(first: 100, orderBy: totalValueLockedUSD, orderDirection: desc) {
      id
      feeTier
      txCount
      volumeUSD
      feesUSD
      totalValueLockedUSD
      totalValueLockedToken0
      totalValueLockedToken1
      token0 {
        id
        symbol
        name
        derivedETH
      }
      token1 {
        id
        symbol
        name
        derivedETH
      }
      swaps(first: 1000, orderBy: timestamp, orderDirection: desc) {
        amount0
        amount1
        amountUSD
      }
    }
    tokens(first: 100, orderBy: totalValueLockedUSD, orderDirection: desc) {
      id
      symbol
      name
      txCount
      volumeUSD
      feesUSD
      totalValueLocked
      totalValueLockedUSD
      derivedETH
    }
    bundles(first: 1) {
      ethPriceUSD
    }
    uniswapDayDatas(first: 300, orderBy: date, orderDirection: desc) {
      id
      date
      tvlUSD
      volumeUSD
      feesUSD
      txCount
    }
    swaps(first: 20, skip: $swapSkip, orderBy: timestamp, orderDirection: desc) {
      id
      timestamp
      origin
      amount0
      amount1
      amountUSD
      transaction {
        id
      }
      token0 {
        id
        symbol
        name
        derivedETH
      }
      token1 {
        id
        symbol
        name
        derivedETH
      }
    }
    recentTransactions: transactions(
      first: 1000
      orderBy: timestamp
      orderDirection: desc
      where: { timestamp_gte: $activityStart }
    ) {
      id
      timestamp
    }
    chartSwaps: swaps(first: 1000, orderBy: timestamp, orderDirection: desc, where: { timestamp_gte: $chartStart }) {
      id
      timestamp
      amount0
      amount1
      amountUSD
      transaction {
        id
      }
      token0 {
        id
        symbol
        name
        derivedETH
      }
      token1 {
        id
        symbol
        name
        derivedETH
      }
      origin
    }
  }
`

const V3_ALL_TIME_CHART_QUERY = gql`
  query V3AllTimeChart {
    recentDays: uniswapDayDatas(first: 1000, orderBy: date, orderDirection: desc) {
      id
      date
      tvlUSD
      volumeUSD
      feesUSD
      txCount
    }
    olderDays: uniswapDayDatas(first: 1000, skip: 1000, orderBy: date, orderDirection: desc) {
      id
      date
      tvlUSD
      volumeUSD
      feesUSD
      txCount
    }
  }
`

const V2_ALL_TIME_CHART_QUERY = gql`
  query V2AllTimeChart {
    recentDays: uniswapDayDatas(first: 1000, orderBy: date, orderDirection: desc) {
      id
      date
      dailyVolumeUSD
      totalLiquidityUSD
      txCount
    }
    olderDays: uniswapDayDatas(first: 1000, skip: 1000, orderBy: date, orderDirection: desc) {
      id
      date
      dailyVolumeUSD
      totalLiquidityUSD
      txCount
    }
  }
`

const V3_INTRADAY_LIQUIDITY_QUERY = gql`
  query V3IntradayLiquidity($start: Int!) {
    factories(first: 1) {
      totalValueLockedUSD
    }
    pools(first: 1000) {
      id
      totalValueLockedUSD
    }
    poolHourDatas(first: 1000, orderBy: periodStartUnix, orderDirection: desc, where: { periodStartUnix_gte: $start }) {
      periodStartUnix
      tvlUSD
      pool {
        id
      }
    }
  }
`

const V2_INTRADAY_LIQUIDITY_QUERY = gql`
  query V2IntradayLiquidity($start: Int!) {
    uniswapFactories(where: { id: "${V2_FACTORY_ADDRESS}" }) { totalLiquidityUSD }
    pairs(first: 1000) { id reserveUSD }
    pairHourDatas(first: 1000, orderBy: hourStartUnix, orderDirection: desc, where: { hourStartUnix_gte: $start }) {
      hourStartUnix
      reserveUSD
      pair { id }
    }
  }
`

interface FactoryAnalytics {
  id: string
  poolCount: string
  txCount: string
  totalVolumeUSD: string
  totalFeesUSD: string
  totalValueLockedUSD: string
}

interface TokenAnalytics {
  id: string
  symbol: string | null
  name: string | null
  txCount: string
  volumeUSD: string
  untrackedVolumeUSD: string
  feesUSD: string
  totalValueLocked: string
  totalValueLockedUSD: string
  derivedETH: string
}

interface PoolAnalytics {
  id: string
  feeTier: string
  txCount: string
  volumeUSD: string
  untrackedVolumeUSD: string
  feesUSD: string
  totalValueLockedUSD: string
  totalValueLockedToken0: string
  totalValueLockedToken1: string
  token0: Pick<TokenAnalytics, 'id' | 'symbol' | 'name' | 'derivedETH'>
  token1: Pick<TokenAnalytics, 'id' | 'symbol' | 'name' | 'derivedETH'>
  swaps: { amount0: string; amount1: string; amountUSD: string }[]
}

interface DayAnalytics {
  id: string
  date: number
  tvlUSD: string
  volumeUSD: string
  feesUSD: string
  txCount: string
}

interface SwapAnalytics {
  id: string
  timestamp: string
  origin: string
  amount0: string
  amount1: string
  amountUSD: string
  transaction: { id: string }
  token0: Pick<TokenAnalytics, 'id' | 'symbol' | 'name' | 'derivedETH'>
  token1: Pick<TokenAnalytics, 'id' | 'symbol' | 'name' | 'derivedETH'>
}

interface V3AnalyticsData {
  _meta?: {
    block: { number: number }
    hasIndexingErrors: boolean
  }
  factories: FactoryAnalytics[]
  pools: PoolAnalytics[]
  tokens: TokenAnalytics[]
  bundles: { ethPriceUSD: string }[]
  uniswapDayDatas: DayAnalytics[]
  swaps: SwapAnalytics[]
  recentTransactions: TimestampedTransaction[]
  chartSwaps: SwapAnalytics[]
}

interface TimestampedTransaction {
  id: string
  timestamp: string
}

interface V3AllTimeChartData {
  recentDays: DayAnalytics[]
  olderDays: DayAnalytics[]
}

interface V2TokenAnalytics {
  id: string
  symbol: string | null
  name: string | null
  txCount: string
  tradeVolumeUSD: string
  totalLiquidity: string
  derivedETH: string
}

interface V2PairAnalytics {
  id: string
  txCount: string
  reserve0: string
  reserve1: string
  reserveUSD: string
  volumeUSD: string
  token0: Pick<V2TokenAnalytics, 'id' | 'symbol' | 'name'>
  token1: Pick<V2TokenAnalytics, 'id' | 'symbol' | 'name'>
}

interface V2DayAnalytics {
  id: string
  date: number
  dailyVolumeUSD: string
  totalLiquidityUSD: string
  txCount: string
}

interface V2SwapAnalytics {
  amountUSD: string
  amount0In: string
  amount0Out: string
  amount1In: string
  amount1Out: string
  to: string
  pair: {
    token0: { symbol: string | null; derivedETH: string }
    token1: { symbol: string | null; derivedETH: string }
  }
}

interface V2TransactionAnalytics {
  id: string
  timestamp: string
  swaps: V2SwapAnalytics[]
}

interface V2AnalyticsData {
  _meta?: {
    block: { number: number }
    hasIndexingErrors: boolean
  }
  uniswapFactories: {
    id: string
    pairCount: string
    txCount: string
    totalVolumeUSD: string
    totalLiquidityUSD: string
    totalLiquidityETH: string
  }[]
  bundle: { ethPrice: string } | null
  pairs: V2PairAnalytics[]
  tokens: V2TokenAnalytics[]
  uniswapDayDatas: V2DayAnalytics[]
  transactions: V2TransactionAnalytics[]
  recentTransactions: TimestampedTransaction[]
  chartTransactions: V2TransactionAnalytics[]
}

interface V2AllTimeChartData {
  recentDays: V2DayAnalytics[]
  olderDays: V2DayAnalytics[]
}

interface IntradayLiquiditySnapshot {
  periodStartUnix?: number
  hourStartUnix?: number
  tvlUSD?: string
  reserveUSD?: string
  pool?: { id: string }
  pair?: { id: string }
}

interface V3IntradayLiquidityData {
  factories: { totalValueLockedUSD: string }[]
  pools: { id: string; totalValueLockedUSD: string }[]
  poolHourDatas: IntradayLiquiditySnapshot[]
}

interface V2IntradayLiquidityData {
  uniswapFactories: { totalLiquidityUSD: string }[]
  pairs: { id: string; reserveUSD: string }[]
  pairHourDatas: IntradayLiquiditySnapshot[]
}

type AnalyticsView = 'combined' | 'v2' | 'v3'
type ChartRange = '1d' | '1w' | '1y' | 'all'
const SWAPS_PER_PAGE = 20
const SWAP_PAGE_COUNT = 10
const TOP_LIST_PER_PAGE = 10
const CHART_RANGE_DAYS: Record<Exclude<ChartRange, 'all'>, number> = { '1d': 1, '1w': 7, '1y': 365 }
const GLOBAL_VOLUME_YEARLY_KEEP_RATIO = 0.3
const GLOBAL_VOLUME_ALL_TIME_POINTS = 120

interface AnalyticsChartDay {
  id: string
  date: number
  liquidityUSD: number
  volumeUSD: number
  transactions: number
}

const TRANSACTION_EXPLORER_URL = 'https://explorer.harmony.one/tx/'
const ADDRESS_EXPLORER_URL = 'https://explorer.harmony.one/address/'

const Page = styled.main`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1520px;
  min-width: 0;
  box-sizing: border-box;
  padding: 17px 16px 48px;

  @media only screen and (max-width: ${({ theme }) => `${theme.breakpoint.md}px`}) {
    padding-top: 12px;
  }

  @media only screen and (max-width: ${({ theme }) => `${theme.breakpoint.sm}px`}) {
    padding: 5px 12px 32px;
  }
`

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;

  @media only screen and (max-width: ${({ theme }) => `${theme.breakpoint.sm}px`}) {
    flex-direction: column;
    gap: 12px;
  }
`

const ViewSelector = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
  max-width: 100%;
  width: fit-content;
  margin-bottom: 24px;
  padding: 4px;
  background: ${({ theme }) => theme.backgroundSurface};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 14px;
`

const ViewButton = styled.button<{ $active: boolean }>`
  min-height: 38px;
  padding: 7px 14px;
  color: ${({ $active, theme }) => ($active ? theme.accentAction : theme.textSecondary)};
  background: ${({ $active, theme }) => ($active ? theme.accentActionSoft : 'transparent')};
  border: 0;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease, filter 120ms ease;

  &:hover {
    color: ${({ theme }) => theme.accentAction};
    background: ${({ theme }) => theme.accentActionSoft};
    filter: brightness(1.1);
  }
`

const LegacyViewLink = styled.a`
  display: inline-flex;
  align-items: center;
  min-height: 38px;
  padding: 7px 14px;
  color: ${({ theme }) => theme.textSecondary};
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.accentAction};
    background: ${({ theme }) => theme.accentActionSoft};
  }
`

const SyncingPanel = styled.div`
  margin-bottom: 24px;
  padding: 18px;
  color: ${({ theme }) => theme.textPrimary};
  background: ${({ theme }) => theme.accentActionSoft};
  border: 1px solid ${({ theme }) => theme.accentAction};
  border-radius: 16px;
`

const SyncingHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  font-size: 17px;
  font-weight: 600;

  svg {
    animation: analytics-sync-spin 1.5s linear infinite;
  }

  @keyframes analytics-sync-spin {
    to {
      transform: rotate(360deg);
    }
  }
`

const SyncingDetail = styled.div`
  color: ${({ theme }) => theme.textSecondary};
  font-size: 14px;
  line-height: 22px;
`

const HeaderCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`

const Status = styled.div`
  color: ${({ theme }) => theme.textSecondary};
  font-size: 13px;
  line-height: 20px;
  overflow-wrap: anywhere;
`

const RefreshButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  padding: 8px 12px;
  color: ${({ theme }) => theme.textPrimary};
  background: ${({ theme }) => theme.backgroundSurface};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    border-color: ${({ theme }) => theme.accentAction};
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`

const MetricGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 24px;

  @media only screen and (max-width: ${({ theme }) => `${theme.breakpoint.md}px`}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media only screen and (max-width: ${({ theme }) => `${theme.breakpoint.sm}px`}) {
    grid-template-columns: 1fr;
  }
`

const MetricCard = styled.div`
  min-width: 0;
  padding: 18px;
  background: ${({ theme }) => theme.backgroundSurface};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 16px;
`

const MetricLabel = styled.div`
  margin-bottom: 8px;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 13px;
  font-weight: 500;
`

const MetricValue = styled.div`
  overflow: hidden;
  color: ${({ theme }) => theme.textPrimary};
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const MetricDetail = styled.div`
  margin-top: 4px;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 12px;
`

const ProtocolBreakdown = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 24px;

  @media only screen and (max-width: ${({ theme }) => `${theme.breakpoint.sm}px`}) {
    grid-template-columns: 1fr;
  }
`

const TrendGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 24px;

  @media only screen and (max-width: ${({ theme }) => `${theme.breakpoint.md}px`}) {
    grid-template-columns: 1fr;
  }
`

const TrendCard = styled.div<{ $wide?: boolean }>`
  min-width: 0;
  padding: 18px;
  background: ${({ theme }) => theme.backgroundSurface};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 16px;

  ${({ $wide }) => $wide && 'grid-column: 1 / -1;'}
`

const TrendHeading = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
`

const Bars = styled.div<{ $count: number }>`
  display: flex;
  align-items: flex-end;
  gap: ${({ $count }) => ($count > 180 ? '0' : $count > 60 ? '1px' : '5px')};
  height: 150px;
  min-width: 0;
  overflow: hidden;
`

const Bar = styled.button`
  flex: 1;
  min-width: 0;
  border-radius: 4px 4px 1px 1px;
  background: linear-gradient(180deg, #d86624 0%, #8f3d16 100%);
  border: 0;
  padding: 0;
  cursor: crosshair;
  transition: opacity 100ms ease;

  &:hover,
  &:focus-visible {
    background: rgba(246, 160, 79, 0.55);
    outline: none;
  }
`

const TrendLineArea = styled.div`
  position: relative;
  height: 150px;
  cursor: crosshair;
`

const TrendLine = styled.svg`
  width: 100%;
  height: 150px;
  overflow: visible;
  pointer-events: none;

  polyline {
    fill: none;
    stroke: #d86624;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-width: 2.5;
  }
`

const TrendCrosshairX = styled.div<{ $left: number }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${({ $left }) => `${$left}%`};
  width: 1px;
  background: ${({ theme }) => theme.backgroundOutline};
  pointer-events: none;
`

const TrendCrosshairY = styled.div<{ $top: number }>`
  position: absolute;
  top: ${({ $top }) => `${$top}%`};
  right: 0;
  left: 0;
  height: 1px;
  background: ${({ theme }) => theme.backgroundOutline};
  pointer-events: none;
`

const TrendPoint = styled.div<{ $left: number; $top: number }>`
  position: absolute;
  top: ${({ $top }) => `${$top}%`};
  left: ${({ $left }) => `${$left}%`};
  width: 10px;
  height: 10px;
  background: #d86624;
  border: 2px solid ${({ theme }) => theme.backgroundSurface};
  border-radius: 50%;
  box-shadow: 0 0 0 1px #d86624;
  pointer-events: none;
  transform: translate(-50%, -50%);
`

const ChartLabels = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 11px;
`

const SearchInput = styled.input`
  width: min(100%, 360px);
  min-height: 42px;
  margin-bottom: 12px;
  padding: 9px 13px;
  color: ${({ theme }) => theme.textPrimary};
  background: ${({ theme }) => theme.backgroundSurface};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 12px;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.accentAction};
  }
`

const ReserveDetail = styled.div`
  margin-top: 4px;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 12px;
`

const Notice = styled.div<{ $error?: boolean }>`
  margin-bottom: 24px;
  padding: 16px;
  color: ${({ $error, theme }) => ($error ? theme.accentFailure : theme.textSecondary)};
  background: ${({ theme }) => theme.backgroundSurface};
  border: 1px solid ${({ $error, theme }) => ($error ? theme.accentFailure : theme.backgroundOutline)};
  border-radius: 14px;
  font-size: 14px;
  line-height: 21px;
  overflow-wrap: anywhere;
`

const Section = styled.section`
  margin-top: 24px;
`

const SectionTitle = styled.h2`
  margin: 0 0 12px;
  color: ${({ theme }) => theme.textPrimary};
  font-size: 20px;
  font-weight: 600;
`

const TableCard = styled.div`
  position: relative;
  overflow-x: auto;
  background: ${({ theme }) => theme.backgroundSurface};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 16px;
`

const StableTableCard = styled(TableCard)<{ $minHeight: number }>`
  min-height: ${({ $minHeight }) => `${$minHeight}px`};
`

const TableLoading = styled.div`
  position: absolute;
  z-index: 1;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: grid;
  color: ${({ theme }) => theme.accentAction};
  background: ${({ theme }) => theme.backgroundSurface};
  font-size: 14px;
  font-weight: 600;
  opacity: 0.88;
  place-items: center;
`

const Table = styled.table`
  width: 100%;
  min-width: 700px;
  border-collapse: collapse;

  th,
  td {
    padding: 14px 16px;
    border-bottom: 1px solid ${({ theme }) => theme.backgroundOutline};
    text-align: right;
    white-space: nowrap;
  }

  th:first-child,
  td:first-child {
    text-align: left;
  }

  th {
    color: ${({ theme }) => theme.textSecondary};
    font-size: 12px;
    font-weight: 500;
  }

  td {
    color: ${({ theme }) => theme.textPrimary};
    font-size: 14px;
  }

  tr:last-child td {
    border-bottom: 0;
  }
`

const RecentSwapsTable = styled(Table)`
  th,
  td {
    padding: 13px 16px;
    font-size: 13px;
    text-align: left;
  }

  tbody tr {
    transition: background 100ms ease;
  }

  tbody tr:hover {
    background: ${({ theme }) => theme.accentActionSoft};
  }
`

const EmptyRow = styled.td`
  height: 80px;
  color: ${({ theme }) => theme.textSecondary} !important;
  text-align: center !important;
`

const Address = styled.div`
  margin-top: 3px;
  color: ${({ theme }) => theme.textSecondary};
  font-family: ${({ theme }) => theme.fonts.code};
  font-size: 11px;
`

const ExplorerLink = styled.a`
  color: ${({ theme }) => theme.accentAction};
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.accentAction};
  }
`

const PoolLink = styled(Link)`
  color: ${({ theme }) => theme.textPrimary};
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.accentAction};
  }
`

const Pagination = styled.nav`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  margin-top: 12px;
`

const PageButton = styled.button<{ $active?: boolean }>`
  min-width: 34px;
  min-height: 34px;
  padding: 6px 9px;
  color: ${({ $active, theme }) => ($active ? theme.accentAction : theme.textSecondary)};
  background: ${({ $active, theme }) => ($active ? theme.accentActionSoft : theme.backgroundSurface)};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 9px;
  cursor: pointer;

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`

const compactNumber = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 2,
})

const wholeNumber = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 0,
})

function formatNumber(value: string | number | undefined, prefix = ''): string {
  if (value === undefined) return '-'
  const numericValue = Number(value)
  if (!Number.isFinite(numericValue)) return '-'
  return `${prefix}${compactNumber.format(numericValue)}`
}

function formatFeeTier(feeTier: string): string {
  const numericFee = Number(feeTier)
  return Number.isFinite(numericFee) ? `${numericFee / 10_000}%` : '-'
}

function shortAddress(address: string): string {
  return `${address.slice(0, 6)}...${address.slice(-4)}`
}

function tokenLabel(token: Pick<TokenAnalytics, 'symbol' | 'name'>): string {
  return token.symbol || token.name || 'Unknown token'
}

function formatSwapUsd(value: number): string {
  const numericValue = Math.abs(value)
  if (!Number.isFinite(numericValue) || numericValue === 0) return '-'
  if (numericValue > 0 && numericValue < 0.01) return '<$0.01'
  return `$${numericValue.toLocaleString(undefined, {
    maximumSignificantDigits: 6,
  })}`
}

export function getSwapUsdValue(swap: SwapAnalytics, wonePriceUSD: number): number {
  const indexedAmountUSD = Math.abs(Number(swap.amountUSD))
  if (Number.isFinite(indexedAmountUSD) && indexedAmountUSD > 0) return indexedAmountUSD

  const { inputAmount, inputToken } = getSwapAmounts(swap)
  const derivedWONE = Math.abs(Number(inputToken.derivedETH))
  const derivedAmountUSD = inputAmount * derivedWONE * wonePriceUSD
  return Number.isFinite(derivedAmountUSD) ? derivedAmountUSD : 0
}

function getPoolVolumeUsd(pool: PoolAnalytics, wonePriceUSD: number): number {
  const indexedVolume = Math.max(Math.abs(Number(pool.volumeUSD)), Math.abs(Number(pool.untrackedVolumeUSD)))
  const swapVolume = pool.swaps.reduce((total, swap) => {
    const indexedSwapValue = Math.abs(Number(swap.amountUSD))
    if (Number.isFinite(indexedSwapValue) && indexedSwapValue > 0) return total + indexedSwapValue
    const token0Value = Math.abs(Number(swap.amount0)) * Math.abs(Number(pool.token0.derivedETH)) * wonePriceUSD
    const token1Value = Math.abs(Number(swap.amount1)) * Math.abs(Number(pool.token1.derivedETH)) * wonePriceUSD
    const fallbackValue = Math.max(token0Value, token1Value)
    return total + (Number.isFinite(fallbackValue) ? fallbackValue : 0)
  }, 0)
  return Math.max(Number.isFinite(indexedVolume) ? indexedVolume : 0, swapVolume)
}

function getPoolFeesUsd(pool: PoolAnalytics, wonePriceUSD: number): number {
  return Math.max(
    Math.abs(Number(pool.feesUSD)),
    getPoolVolumeUsd(pool, wonePriceUSD) * (Number(pool.feeTier) / 1_000_000)
  )
}

export function getV2SwapUsdValue(swap: V2SwapAnalytics, wonePriceUSD: number): number {
  const indexedAmountUSD = Math.abs(Number(swap.amountUSD))
  if (Number.isFinite(indexedAmountUSD) && indexedAmountUSD > 0) return indexedAmountUSD

  const token0IsInput = Number(swap.amount0In) > 0
  const inputAmount = Math.abs(Number(token0IsInput ? swap.amount0In : swap.amount1In))
  const inputToken = token0IsInput ? swap.pair.token0 : swap.pair.token1
  const derivedWONE = Math.abs(Number(inputToken.derivedETH))
  const derivedAmountUSD = inputAmount * derivedWONE * wonePriceUSD
  return Number.isFinite(derivedAmountUSD) ? derivedAmountUSD : 0
}

export function formatRelativeTime(timestamp: string, now = Date.now()): string {
  const elapsedSeconds = Math.max(0, Math.floor(now / 1000) - Number(timestamp))
  if (!Number.isFinite(elapsedSeconds)) return '-'
  if (elapsedSeconds < 60) return 'just now'
  const minutes = Math.floor(elapsedSeconds / 60)
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
  const daysAgo = Math.floor(hours / 24)
  return `${daysAgo} day${daysAgo === 1 ? '' : 's'} ago`
}

export function getSwapAmounts(swap: SwapAnalytics) {
  const token0IsInput = Number(swap.amount0) > 0
  return {
    inputAmount: Math.abs(Number(token0IsInput ? swap.amount0 : swap.amount1)),
    inputToken: token0IsInput ? swap.token0 : swap.token1,
    outputAmount: Math.abs(Number(token0IsInput ? swap.amount1 : swap.amount0)),
    outputToken: token0IsInput ? swap.token1 : swap.token0,
  }
}

function formatUsdPrice(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '-'
  return `$${value.toLocaleString(undefined, { maximumSignificantDigits: 6 })}`
}

function formatDate(timestamp: number): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(timestamp * 1000))
}

function formatHour(timestamp: number): string {
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric' }).format(
    new Date(timestamp * 1000)
  )
}

function TrendChart({
  title,
  data,
  value,
  formatValue = (trendValue) => formatNumber(trendValue, '$'),
  emptyMessage = 'Historical USD data will appear after the pricing anchor is reindexed.',
  wide = false,
  formatTimestamp = formatDate,
  kind = 'bar',
}: {
  title: string
  data: AnalyticsChartDay[]
  value: (day: AnalyticsChartDay) => number
  formatValue?: (value: number) => string
  emptyMessage?: string
  wide?: boolean
  formatTimestamp?: (timestamp: number) => string
  kind?: 'bar' | 'line'
}) {
  const [hoveredDay, setHoveredDay] = useState<AnalyticsChartDay | null>(null)
  const maximum = Math.max(...data.map(value), 0)
  const minimum = Math.min(...data.map(value), maximum)
  const range = maximum - minimum
  const latest = data[data.length - 1]
  const displayedDay = hoveredDay ?? latest
  const hoveredIndex = hoveredDay ? data.indexOf(hoveredDay) : -1
  const hoveredX = hoveredIndex < 0 || data.length <= 1 ? 50 : (hoveredIndex / (data.length - 1)) * 100
  const hoveredY = hoveredDay && range > 0 ? 96 - ((value(hoveredDay) - minimum) / range) * 92 : 50
  const linePoints = data
    .map((day, index) => {
      const x = data.length === 1 ? 50 : (index / (data.length - 1)) * 100
      const y = range > 0 ? 96 - ((value(day) - minimum) / range) * 92 : 50
      return `${x},${y}`
    })
    .join(' ')
  const areaPoints = `${data.length === 1 ? 50 : 0},100 ${linePoints} ${data.length === 1 ? 50 : 100},100`

  return (
    <TrendCard $wide={wide}>
      <TrendHeading>
        <ThemedText.DeprecatedSubHeader fontWeight={600}>{title}</ThemedText.DeprecatedSubHeader>
        <ThemedText.DeprecatedBody color="textSecondary">
          {displayedDay ? (
            <>
              {formatTimestamp(displayedDay.date)}: <strong>{formatValue(value(displayedDay))}</strong>
            </>
          ) : (
            '-'
          )}
        </ThemedText.DeprecatedBody>
      </TrendHeading>
      {data.length ? (
        <>
          {kind === 'line' ? (
            <TrendLineArea
              onMouseLeave={() => setHoveredDay(null)}
              onMouseMove={(event) => {
                const bounds = event.currentTarget.getBoundingClientRect()
                const ratio = Math.max(0, Math.min(1, (event.clientX - bounds.left) / bounds.width))
                setHoveredDay(data[Math.round(ratio * Math.max(data.length - 1, 0))])
              }}
            >
              <TrendLine viewBox="0 0 100 100" preserveAspectRatio="none" role="img" aria-label={`${title} chart`}>
                <defs>
                  <linearGradient id="analytics-trend-area" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#F6A04F" stopOpacity="0.99" />
                    <stop offset="100%" stopColor="#F6A04F" stopOpacity="0.01" />
                  </linearGradient>
                </defs>
                <polygon points={areaPoints} fill="url(#analytics-trend-area)" />
                <polyline points={linePoints} vectorEffect="non-scaling-stroke" />
              </TrendLine>
              {hoveredDay && (
                <>
                  <TrendCrosshairX $left={hoveredX} />
                  <TrendCrosshairY $top={hoveredY} />
                  <TrendPoint $left={hoveredX} $top={hoveredY} />
                </>
              )}
            </TrendLineArea>
          ) : (
            <Bars $count={data.length} onMouseLeave={() => setHoveredDay(null)}>
              {data.map((day) => {
                const dayValue = value(day)
                const height = maximum > 0 ? Math.max((dayValue / maximum) * 100, 3) : 3
                return (
                  <Bar
                    key={day.id}
                    type="button"
                    style={{ height: `${height}%` }}
                    title={`${formatTimestamp(day.date)}: ${formatValue(dayValue)}`}
                    aria-label={`${formatTimestamp(day.date)}: ${formatValue(dayValue)}`}
                    onMouseEnter={() => setHoveredDay(day)}
                    onFocus={() => setHoveredDay(day)}
                  />
                )
              })}
            </Bars>
          )}
          <ChartLabels>
            <span>{formatTimestamp(data[0].date)}</span>
            <span>{formatTimestamp(latest.date)}</span>
          </ChartLabels>
        </>
      ) : (
        <Notice>{emptyMessage}</Notice>
      )}
    </TrendCard>
  )
}

function filterChartDays(days: AnalyticsChartDay[], range: ChartRange): AnalyticsChartDay[] {
  if (range === 'all') return days
  return days.slice(-CHART_RANGE_DAYS[range])
}

function bucketChartDays(days: AnalyticsChartDay[], pointCount: number): AnalyticsChartDay[] {
  if (days.length <= pointCount) return days

  return Array.from({ length: pointCount }, (_, index) => {
    const start = Math.floor((index * days.length) / pointCount)
    const end = Math.floor(((index + 1) * days.length) / pointCount)
    const bucket = days.slice(start, Math.max(end, start + 1))
    const latest = bucket[bucket.length - 1]

    return {
      id: `bucket-${bucket[0].id}-${latest.id}`,
      date: latest.date,
      liquidityUSD: latest.liquidityUSD,
      volumeUSD: bucket.reduce((total, day) => total + day.volumeUSD, 0),
      transactions: bucket.reduce((total, day) => total + day.transactions, 0),
    }
  })
}

function combineProtocolDays(v2Days: AnalyticsChartDay[], v3Days: AnalyticsChartDay[]): AnalyticsChartDay[] {
  const v2ByDate = new Map(v2Days.map((day) => [day.date, day]))
  const v3ByDate = new Map(v3Days.map((day) => [day.date, day]))
  const dates = [...new Set([...v2ByDate.keys(), ...v3ByDate.keys()])].sort((first, second) => first - second)
  let v2LiquidityUSD = 0
  let v3LiquidityUSD = 0

  return dates.map((date) => {
    const v2Day = v2ByDate.get(date)
    const v3Day = v3ByDate.get(date)
    if (v2Day) v2LiquidityUSD = v2Day.liquidityUSD
    if (v3Day) v3LiquidityUSD = v3Day.liquidityUSD
    return {
      id: `combined-${date}`,
      date,
      liquidityUSD: v2LiquidityUSD + v3LiquidityUSD,
      volumeUSD: (v2Day?.volumeUSD ?? 0) + (v3Day?.volumeUSD ?? 0),
      transactions: (v2Day?.transactions ?? 0) + (v3Day?.transactions ?? 0),
    }
  })
}

function reconstructHourlyLiquidity(
  start: number,
  currentTotal: number,
  currentValues: { id: string; value: string }[],
  snapshots: { timestamp: number; id: string; value: string }[]
): number[] {
  const valuesById = new Map(currentValues.map(({ id, value }) => [id, Number(value)]))
  let total = Number.isFinite(currentTotal) ? currentTotal : 0
  const snapshotsByHour = new Map<number, typeof snapshots>()
  for (const snapshot of snapshots) {
    const hour = Math.floor((snapshot.timestamp - start) / 3_600)
    if (hour < 0 || hour > 23) continue
    const rows = snapshotsByHour.get(hour) ?? []
    rows.push(snapshot)
    snapshotsByHour.set(hour, rows)
  }
  return Array.from({ length: 24 }, (_, index) => index)
    .reverse()
    .reduce<number[]>(
      (history, hour) => {
        history[hour] = total
        for (const snapshot of snapshotsByHour.get(hour) ?? []) {
          const previous = valuesById.get(snapshot.id) ?? 0
          const next = Number(snapshot.value)
          if (!Number.isFinite(next)) continue
          total += next - previous
          valuesById.set(snapshot.id, next)
        }
        return history
      },
      Array.from({ length: 24 }, () => total)
    )
}

export default function Analytics() {
  const { chainId } = useWeb3React()
  const activeBlock = useBlockNumber()
  const [view, setView] = useState<AnalyticsView>('combined')
  const [chartRange, setChartRange] = useState<ChartRange>('1w')
  const [search, setSearch] = useState('')
  const [v3Page, setV3Page] = useState(0)
  const [v2Page, setV2Page] = useState(0)
  const [combinedPage, setCombinedPage] = useState(0)
  const [v3PoolsPage, setV3PoolsPage] = useState(0)
  const [v3TokensPage, setV3TokensPage] = useState(0)
  const [v2PairsPage, setV2PairsPage] = useState(0)
  const [v2TokensPage, setV2TokensPage] = useState(0)
  const [quickLoading, setQuickLoading] = useState(false)
  const [chartStart] = useState(() => `${Math.floor(Date.now() / 3_600_000) * 3_600 - 23 * 3_600}`)
  const [activityNow, setActivityNow] = useState(() => Date.now())
  const activityStart = `${Math.floor(activityNow / 1_000) - 86_400}`
  useEffect(() => {
    const interval = window.setInterval(() => setActivityNow(Date.now()), 30_000)
    return () => window.clearInterval(interval)
  }, [])
  const { data, error, loading, refetch } = useQuery<V3AnalyticsData>(V3_ANALYTICS_QUERY, {
    client: v3AnalyticsApolloClient,
    variables: { swapSkip: (view === 'combined' ? combinedPage : v3Page) * SWAPS_PER_PAGE, chartStart, activityStart },
    pollInterval: 30_000,
    notifyOnNetworkStatusChange: true,
  })
  const {
    data: v2Data,
    error: v2Error,
    loading: v2Loading,
    refetch: refetchV2,
  } = useQuery<V2AnalyticsData>(V2_ANALYTICS_QUERY, {
    client: v2AnalyticsApolloClient,
    variables: {
      transactionSkip: (view === 'combined' ? combinedPage : v2Page) * SWAPS_PER_PAGE,
      chartStart,
      activityStart,
    },
    pollInterval: 30_000,
    notifyOnNetworkStatusChange: true,
  })
  const { data: v3AllTimeData } = useQuery<V3AllTimeChartData>(V3_ALL_TIME_CHART_QUERY, {
    client: v3AnalyticsApolloClient,
    skip: chartRange !== 'all' || view === 'v2',
  })
  const { data: v2AllTimeData } = useQuery<V2AllTimeChartData>(V2_ALL_TIME_CHART_QUERY, {
    client: v2AnalyticsApolloClient,
    skip: chartRange !== 'all' || view === 'v3',
  })
  const { data: v3IntradayLiquidityData } = useQuery<V3IntradayLiquidityData>(V3_INTRADAY_LIQUIDITY_QUERY, {
    client: v3AnalyticsApolloClient,
    variables: { start: Number(chartStart) },
    skip: chartRange !== '1d' || view === 'v2',
    pollInterval: 30_000,
  })
  const { data: v2IntradayLiquidityData } = useQuery<V2IntradayLiquidityData>(V2_INTRADAY_LIQUIDITY_QUERY, {
    client: v2AnalyticsApolloClient,
    variables: { start: Number(chartStart) },
    skip: chartRange !== '1d' || view === 'v3',
    pollInterval: 30_000,
  })

  const factory = data?.factories[0]
  const v2Factory = v2Data?.uniswapFactories[0]
  const wonePriceUSD = Number(data?.bundles[0]?.ethPriceUSD ?? 0)
  const v2WcroPriceUSD = Number(v2Data?.bundle?.ethPrice ?? 0)
  const indexedBlock = data?._meta?.block.number
  const v2IndexedBlock = v2Data?._meta?.block.number
  const chainHeadBlock = chainId === SupportedChainId.HARMONY ? activeBlock : undefined
  const syncingProtocols = [
    ...(view !== 'v3' && chainHeadBlock && v2IndexedBlock && chainHeadBlock - v2IndexedBlock > 60
      ? [
          {
            label: 'V2',
            indexedBlock: v2IndexedBlock,
            blocksBehind: chainHeadBlock - v2IndexedBlock,
          },
        ]
      : []),
    ...(view !== 'v2' && chainHeadBlock && indexedBlock && chainHeadBlock - indexedBlock > 60
      ? [
          {
            label: 'V3',
            indexedBlock,
            blocksBehind: chainHeadBlock - indexedBlock,
          },
        ]
      : []),
  ]
  const hasNoIndexedEntities = !loading && !error && !factory && !data?.pools.length && !data?.tokens.length
  const pricingUnavailable = !loading && !!factory && wonePriceUSD <= 0
  const days = useMemo<AnalyticsChartDay[]>(() => {
    const rawDays = [...(data?.uniswapDayDatas ?? [])].reverse()
    return rawDays.slice(1).map((day, index) => ({
      id: `v3-${day.id}`,
      date: day.date,
      liquidityUSD: Number(day.tvlUSD),
      volumeUSD: Number(day.volumeUSD),
      transactions: Math.max(Number(day.txCount) - Number(rawDays[index].txCount), 0),
    }))
  }, [data?.uniswapDayDatas])
  const v2Days = useMemo<AnalyticsChartDay[]>(() => {
    const rawDays = [...(v2Data?.uniswapDayDatas ?? [])].reverse()
    return rawDays.slice(1).map((day, index) => ({
      id: `v2-${day.id}`,
      date: day.date,
      liquidityUSD: Number(day.totalLiquidityUSD),
      volumeUSD: Number(day.dailyVolumeUSD),
      transactions: Math.max(Number(day.txCount) - Number(rawDays[index].txCount), 0),
    }))
  }, [v2Data?.uniswapDayDatas])
  const allTimeDays = useMemo<AnalyticsChartDay[]>(() => {
    const rawDays = [...(v3AllTimeData?.recentDays ?? []), ...(v3AllTimeData?.olderDays ?? [])].sort(
      (first, second) => first.date - second.date
    )
    return rawDays.slice(1).map((day, index) => ({
      id: `v3-${day.id}`,
      date: day.date,
      liquidityUSD: Number(day.tvlUSD),
      volumeUSD: Number(day.volumeUSD),
      transactions: Math.max(Number(day.txCount) - Number(rawDays[index].txCount), 0),
    }))
  }, [v3AllTimeData?.olderDays, v3AllTimeData?.recentDays])
  const allTimeV2Days = useMemo<AnalyticsChartDay[]>(() => {
    const rawDays = [...(v2AllTimeData?.recentDays ?? []), ...(v2AllTimeData?.olderDays ?? [])].sort(
      (first, second) => first.date - second.date
    )
    return rawDays.slice(1).map((day, index) => ({
      id: `v2-${day.id}`,
      date: day.date,
      liquidityUSD: Number(day.totalLiquidityUSD),
      volumeUSD: Number(day.dailyVolumeUSD),
      transactions: Math.max(Number(day.txCount) - Number(rawDays[index].txCount), 0),
    }))
  }, [v2AllTimeData?.olderDays, v2AllTimeData?.recentDays])
  const combinedDays = useMemo(() => combineProtocolDays(v2Days, days), [days, v2Days])
  const allTimeCombinedDays = useMemo(
    () => combineProtocolDays(allTimeV2Days, allTimeDays),
    [allTimeDays, allTimeV2Days]
  )
  const selectedDays =
    chartRange === 'all'
      ? view === 'v2'
        ? allTimeV2Days.length
          ? allTimeV2Days
          : v2Days
        : view === 'v3'
        ? allTimeDays.length
          ? allTimeDays
          : days
        : allTimeCombinedDays.length
        ? allTimeCombinedDays
        : combinedDays
      : view === 'v2'
      ? v2Days
      : view === 'v3'
      ? days
      : combinedDays
  const intradayDays = useMemo<AnalyticsChartDay[]>(() => {
    const start = Number(chartStart)
    const v3HourlyLiquidity = reconstructHourlyLiquidity(
      start,
      Number(v3IntradayLiquidityData?.factories[0]?.totalValueLockedUSD ?? factory?.totalValueLockedUSD),
      (v3IntradayLiquidityData?.pools ?? []).map((pool) => ({ id: pool.id, value: pool.totalValueLockedUSD })),
      (v3IntradayLiquidityData?.poolHourDatas ?? []).map((snapshot) => ({
        timestamp: snapshot.periodStartUnix ?? 0,
        id: snapshot.pool?.id ?? '',
        value: snapshot.tvlUSD ?? '0',
      }))
    )
    const v2HourlyLiquidity = reconstructHourlyLiquidity(
      start,
      Number(v2IntradayLiquidityData?.uniswapFactories[0]?.totalLiquidityUSD ?? v2Factory?.totalLiquidityUSD),
      (v2IntradayLiquidityData?.pairs ?? []).map((pair) => ({ id: pair.id, value: pair.reserveUSD })),
      (v2IntradayLiquidityData?.pairHourDatas ?? []).map((snapshot) => ({
        timestamp: snapshot.hourStartUnix ?? 0,
        id: snapshot.pair?.id ?? '',
        value: snapshot.reserveUSD ?? '0',
      }))
    )
    const buckets = Array.from({ length: 24 }, (_, index) => ({
      id: `hour-${start + index * 3_600}`,
      date: start + index * 3_600,
      liquidityUSD:
        view === 'v2'
          ? v2HourlyLiquidity[index]
          : view === 'v3'
          ? v3HourlyLiquidity[index]
          : v2HourlyLiquidity[index] + v3HourlyLiquidity[index],
      volumeUSD: 0,
      transactions: 0,
    }))
    const add = (timestamp: string, volumeUSD: number) => {
      const bucket = Math.floor((Number(timestamp) - start) / 3_600)
      if (bucket < 0 || bucket >= buckets.length) return
      buckets[bucket].volumeUSD += Number.isFinite(volumeUSD) ? volumeUSD : 0
      buckets[bucket].transactions += 1
    }
    if (view !== 'v2') {
      for (const swap of data?.chartSwaps ?? []) add(swap.timestamp, getSwapUsdValue(swap, wonePriceUSD))
    }
    if (view !== 'v3') {
      for (const transaction of v2Data?.chartTransactions ?? []) {
        add(
          transaction.timestamp,
          transaction.swaps.reduce((total, swap) => total + getV2SwapUsdValue(swap, v2WcroPriceUSD), 0)
        )
      }
    }
    return buckets
  }, [
    chartStart,
    data?.chartSwaps,
    factory,
    v2Data?.chartTransactions,
    v2Factory,
    v2IntradayLiquidityData,
    v2WcroPriceUSD,
    v3IntradayLiquidityData,
    view,
    wonePriceUSD,
  ])
  const rangedDays = chartRange === '1d' ? intradayDays : filterChartDays(selectedDays, chartRange)
  const lineChartDays = useMemo(() => {
    const targetCount =
      chartRange === 'all'
        ? GLOBAL_VOLUME_ALL_TIME_POINTS
        : chartRange === '1y'
        ? Math.max(2, Math.ceil(rangedDays.length * GLOBAL_VOLUME_YEARLY_KEEP_RATIO))
        : rangedDays.length
    return bucketChartDays(rangedDays, targetCount)
  }, [chartRange, rangedDays])
  const selectedTransactions: number | undefined =
    view === 'v2'
      ? Number(v2Factory?.txCount)
      : view === 'v3'
      ? Number(factory?.txCount)
      : v2Factory && factory
      ? Number(v2Factory.txCount) + Number(factory.txCount)
      : undefined
  const cumulativeChartDays = useMemo(() => {
    const rangeTransactions = lineChartDays.reduce((total, day) => total + day.transactions, 0)
    let cumulativeTransactions = Math.max((selectedTransactions ?? rangeTransactions) - rangeTransactions, 0)
    return lineChartDays.map((day) => {
      cumulativeTransactions += day.transactions
      return { ...day, transactions: cumulativeTransactions }
    })
  }, [lineChartDays, selectedTransactions])
  const visibleChartDays = cumulativeChartDays
  // Keep all-time charts readable, but never group the day/week/year views:
  // their bars must match the corresponding indexed-day metrics.
  const volumeChartDays = chartRange === 'all' ? bucketChartDays(rangedDays, GLOBAL_VOLUME_ALL_TIME_POINTS) : rangedDays
  const latestDay = days[days.length - 1]
  const latestV2Day = v2Days[v2Days.length - 1]
  const latestSelectedDay = selectedDays[selectedDays.length - 1]
  const selectedLiquidity: number | undefined =
    view === 'v2'
      ? Number(v2Factory?.totalLiquidityUSD)
      : view === 'v3'
      ? Number(factory?.totalValueLockedUSD)
      : v2Factory && factory
      ? Number(v2Factory.totalLiquidityUSD) + Number(factory.totalValueLockedUSD)
      : undefined
  const selectedVolume: number | undefined =
    view === 'v2'
      ? Number(v2Factory?.totalVolumeUSD)
      : view === 'v3'
      ? Number(factory?.totalVolumeUSD)
      : v2Factory && factory
      ? Number(v2Factory.totalVolumeUSD) + Number(factory.totalVolumeUSD)
      : undefined
  const v3TransactionsToday = data?.recentTransactions.length
  const v2TransactionsToday = v2Data?.recentTransactions.length
  const selectedTransactionsToday: number | undefined =
    view === 'v2'
      ? v2TransactionsToday
      : view === 'v3'
      ? v3TransactionsToday
      : v2TransactionsToday !== undefined && v3TransactionsToday !== undefined
      ? v2TransactionsToday + v3TransactionsToday
      : undefined
  const normalizedSearch = search.trim().toLowerCase()
  const pools = useMemo(
    () =>
      (data?.pools ?? []).filter((pool) =>
        [pool.id, pool.token0.symbol, pool.token0.name, pool.token1.symbol, pool.token1.name]
          .filter(Boolean)
          .some((value) => value?.toLowerCase().includes(normalizedSearch))
      ),
    [data?.pools, normalizedSearch]
  )
  const tokens = useMemo(
    () =>
      (data?.tokens ?? []).filter((token) =>
        [token.id, token.symbol, token.name]
          .filter(Boolean)
          .some((value) => value?.toLowerCase().includes(normalizedSearch))
      ),
    [data?.tokens, normalizedSearch]
  )
  const v2Pairs = useMemo(
    () =>
      (v2Data?.pairs ?? []).filter((pair) =>
        [pair.id, pair.token0.symbol, pair.token0.name, pair.token1.symbol, pair.token1.name]
          .filter(Boolean)
          .some((value) => value?.toLowerCase().includes(normalizedSearch))
      ),
    [normalizedSearch, v2Data?.pairs]
  )
  const v2Tokens = useMemo(
    () =>
      (v2Data?.tokens ?? []).filter((token) =>
        [token.id, token.symbol, token.name]
          .filter(Boolean)
          .some((value) => value?.toLowerCase().includes(normalizedSearch))
      ),
    [normalizedSearch, v2Data?.tokens]
  )
  const v2Swaps = useMemo(
    () =>
      (v2Data?.transactions ?? [])
        .flatMap((transaction) => transaction.swaps.map((swap) => ({ ...swap, transaction })))
        .slice(0, 20),
    [v2Data?.transactions]
  )
  const combinedSwaps = useMemo(() => {
    const v3 = (data?.swaps ?? []).map((swap) => {
      const { inputAmount, inputToken, outputAmount, outputToken } = getSwapAmounts(swap)
      return {
        id: swap.id,
        version: 'V3' as const,
        timestamp: swap.timestamp,
        label: `Swap ${tokenLabel(inputToken)} for ${tokenLabel(outputToken)}`,
        value: getSwapUsdValue(swap, wonePriceUSD),
        input: `${formatTokenQuantity(inputAmount)} ${tokenLabel(inputToken)}`,
        output: `${formatTokenQuantity(outputAmount)} ${tokenLabel(outputToken)}`,
        account: swap.origin,
        transaction: swap.transaction.id,
      }
    })
    const v2 = v2Swaps.map(({ transaction, ...swap }, index) => {
      const token0IsInput = Number(swap.amount0In) > 0
      const inputAmount = Number(token0IsInput ? swap.amount0In : swap.amount1In)
      const outputAmount = Number(token0IsInput ? swap.amount1Out : swap.amount0Out)
      const inputToken = token0IsInput ? swap.pair.token0.symbol : swap.pair.token1.symbol
      const outputToken = token0IsInput ? swap.pair.token1.symbol : swap.pair.token0.symbol
      return {
        id: `${transaction.id}-${index}`,
        version: 'V2' as const,
        timestamp: transaction.timestamp,
        label: `Swap ${inputToken || 'token'} for ${outputToken || 'token'}`,
        value: getV2SwapUsdValue(swap, v2WcroPriceUSD),
        input: `${formatTokenQuantity(inputAmount)} ${inputToken || 'token'}`,
        output: `${formatTokenQuantity(outputAmount)} ${outputToken || 'token'}`,
        account: swap.to,
        transaction: transaction.id,
      }
    })
    return [...v3, ...v2]
      .sort((left, right) => Number(right.timestamp) - Number(left.timestamp))
      .slice(0, SWAPS_PER_PAGE)
  }, [data?.swaps, v2Swaps, v2WcroPriceUSD, wonePriceUSD])
  const selectedLoading = view === 'v2' ? v2Loading : view === 'v3' ? loading : loading || v2Loading
  const v3HasNextPage = (data?.swaps.length ?? 0) === SWAPS_PER_PAGE
  const v2HasNextPage = (v2Data?.transactions.length ?? 0) === SWAPS_PER_PAGE
  const combinedHasNextPage = v3HasNextPage || v2HasNextPage

  useEffect(() => {
    if (!quickLoading) return undefined
    const timeout = window.setTimeout(() => setQuickLoading(false), 180)
    return () => window.clearTimeout(timeout)
  }, [quickLoading])

  const selectPage = (setPage: (page: number) => void, page: number) => {
    setQuickLoading(true)
    setPage(page)
  }
  const paged = <T,>(items: T[], page: number) => items.slice(page * TOP_LIST_PER_PAGE, (page + 1) * TOP_LIST_PER_PAGE)

  const handleRefresh = () => {
    if (view !== 'v2') void refetch()
    if (view !== 'v3') void refetchV2()
  }

  return (
    <Page>
      <Header>
        <HeaderCopy>
          <ThemedText.LargeHeader>
            <Trans>ElephantSwap Analytics</Trans>
          </ThemedText.LargeHeader>
          <Status>
            {view === 'combined' ? 'V2 + V3' : view.toUpperCase()} on Harmony ·{' '}
            {view === 'v2'
              ? v2IndexedBlock
                ? `Indexed through Harmony block ${wholeNumber.format(v2IndexedBlock)}`
                : 'Connecting to the V2 subgraph'
              : view === 'v3'
              ? indexedBlock
                ? `Indexed through Harmony block ${wholeNumber.format(indexedBlock)}`
                : 'Connecting to the V3 subgraph'
              : `V2 block ${v2IndexedBlock ? wholeNumber.format(v2IndexedBlock) : '-'} · V3 block ${
                  indexedBlock ? wholeNumber.format(indexedBlock) : '-'
                }`}
            {data?._meta?.hasIndexingErrors || v2Data?._meta?.hasIndexingErrors ? ' · Indexing errors reported' : ''}
          </Status>
        </HeaderCopy>
        <RefreshButton type="button" onClick={handleRefresh} disabled={selectedLoading}>
          <RefreshCw size={16} />
          <Trans>Refresh</Trans>
        </RefreshButton>
      </Header>

      <ViewSelector role="tablist" aria-label="Analytics protocol">
        {(['combined', 'v2', 'v3'] as const).map((option) => (
          <ViewButton
            key={option}
            type="button"
            role="tab"
            aria-selected={view === option}
            $active={view === option}
            onClick={() => setView(option)}
          >
            {option === 'combined' ? 'Combined' : option.toUpperCase()}
          </ViewButton>
        ))}
        <LegacyViewLink href="/info" role="tab" aria-selected="false">
          Legacy
        </LegacyViewLink>
      </ViewSelector>

      {syncingProtocols.length > 0 && chainHeadBlock && (
        <SyncingPanel role="status" aria-live="polite">
          <SyncingHeading>
            <RefreshCw size={18} />
            Analytics subgraph syncing
          </SyncingHeading>
          <SyncingDetail>
            New activity will appear as indexing catches up to Harmony block {wholeNumber.format(chainHeadBlock)}.
          </SyncingDetail>
          {syncingProtocols.map((protocol) => (
            <SyncingDetail key={protocol.label}>
              <strong>{protocol.label}</strong> has reached block {wholeNumber.format(protocol.indexedBlock)} ·{' '}
              {wholeNumber.format(protocol.blocksBehind)} blocks remaining
            </SyncingDetail>
          ))}
        </SyncingPanel>
      )}

      {view !== 'v2' && error && (
        <Notice $error>
          <strong>Unable to load V3 analytics.</strong> {error.message}
          <br />
          Endpoint: {HARMONY_V3_SUBGRAPH_URL}
        </Notice>
      )}

      {view !== 'v3' && v2Error && (
        <Notice $error>
          <strong>Unable to load V2 analytics.</strong> {v2Error.message}
          <br />
          Endpoint: {HARMONY_V2_SUBGRAPH_URL}
        </Notice>
      )}

      {view !== 'v2' && hasNoIndexedEntities && (
        <Notice>
          The V3 subgraph is responding at block {indexedBlock ? wholeNumber.format(indexedBlock) : '-'}, but no
          factory, pool, or token entities have been indexed yet. This page refreshes every 30 seconds and will populate
          as the deployment catches up.
        </Notice>
      )}

      {view !== 'v2' && pricingUnavailable && (
        <Notice $error>
          The subgraph is indexing pools, but its WONE/USDC pricing anchor is still zero. Raw reserves remain visible
          below. Redeploy and reindex the updated V3 subgraph to restore TVL, volume, fee, and token-price metrics.
        </Notice>
      )}

      <ViewSelector role="tablist" aria-label="Chart time range">
        {(['1d', '1w', '1y', 'all'] as const).map((range) => (
          <ViewButton
            key={range}
            type="button"
            role="tab"
            aria-selected={chartRange === range}
            $active={chartRange === range}
            onClick={() => setChartRange(range)}
          >
            {range === 'all' ? 'All time' : range.toUpperCase()}
          </ViewButton>
        ))}
      </ViewSelector>

      <TrendGrid>
        <TrendChart
          title="Liquidity history"
          data={visibleChartDays}
          value={(day) => day.liquidityUSD}
          formatTimestamp={chartRange === '1d' ? formatHour : formatDate}
          kind="line"
          wide
        />
        <TrendChart
          title={
            chartRange === '1d'
              ? 'Daily volume'
              : chartRange === '1w'
              ? 'Weekly volume'
              : chartRange === '1y'
              ? 'Yearly volume'
              : 'All time volume'
          }
          data={volumeChartDays}
          value={(day) => day.volumeUSD}
          formatTimestamp={chartRange === '1d' ? formatHour : formatDate}
        />
        <TrendChart
          title="Transactions (Cumulative)"
          data={visibleChartDays}
          value={(day) => day.transactions}
          formatValue={formatNumber}
          formatTimestamp={chartRange === '1d' ? formatHour : formatDate}
          kind="line"
          emptyMessage="Transaction history will appear after indexed activity is available."
        />
      </TrendGrid>

      <MetricGrid>
        <MetricCard>
          <MetricLabel>Total value locked</MetricLabel>
          <MetricValue>{formatNumber(selectedLiquidity, '$')}</MetricValue>
          <MetricDetail>
            {view === 'combined'
              ? 'V2 + V3 liquidity'
              : `WONE price: ${formatUsdPrice(view === 'v2' ? v2WcroPriceUSD : wonePriceUSD)}`}
          </MetricDetail>
        </MetricCard>
        <MetricCard>
          <MetricLabel>Total volume</MetricLabel>
          <MetricValue>{formatNumber(selectedVolume, '$')}</MetricValue>
        </MetricCard>
        <MetricCard>
          <MetricLabel>{view === 'v3' ? 'Total fees' : 'Latest daily volume'}</MetricLabel>
          <MetricValue>
            {formatNumber(view === 'v3' ? factory?.totalFeesUSD : latestSelectedDay?.volumeUSD, '$')}
          </MetricValue>
        </MetricCard>
        <MetricCard>
          <MetricLabel>{view === 'v3' ? 'Latest indexed day volume' : 'V2 WONE price'}</MetricLabel>
          <MetricValue>
            {view === 'v3' ? formatNumber(latestDay?.volumeUSD, '$') : formatUsdPrice(v2WcroPriceUSD)}
          </MetricValue>
          <MetricDetail>
            {view === 'v3'
              ? latestDay
                ? formatDate(latestDay.date)
                : 'Waiting for daily data'
              : latestV2Day
              ? `Updated ${formatDate(latestV2Day.date)}`
              : 'Waiting for daily data'}
          </MetricDetail>
        </MetricCard>
        <MetricCard>
          <MetricLabel>Transactions (all time)</MetricLabel>
          <MetricValue>{formatNumber(selectedTransactions)}</MetricValue>
        </MetricCard>
        <MetricCard>
          <MetricLabel>Transactions (24h)</MetricLabel>
          <MetricValue>{formatNumber(selectedTransactionsToday)}</MetricValue>
        </MetricCard>
      </MetricGrid>

      {view === 'combined' && (
        <ProtocolBreakdown>
          <MetricCard>
            <MetricLabel>V2 liquidity</MetricLabel>
            <MetricValue>{formatNumber(v2Factory?.totalLiquidityUSD, '$')}</MetricValue>
            <MetricDetail>
              {formatNumber(v2Factory?.pairCount)} pairs · {formatNumber(v2Factory?.txCount)} transactions
            </MetricDetail>
          </MetricCard>
          <MetricCard>
            <MetricLabel>V3 liquidity</MetricLabel>
            <MetricValue>{formatNumber(factory?.totalValueLockedUSD, '$')}</MetricValue>
            <MetricDetail>
              {formatNumber(factory?.poolCount)} pools · {formatNumber(factory?.txCount)} transactions
            </MetricDetail>
          </MetricCard>
        </ProtocolBreakdown>
      )}

      {view === 'combined' && (
        <Section style={{ order: 2 }}>
          <SectionTitle>Latest V2 + V3 swaps</SectionTitle>
          <StableTableCard $minHeight={760}>
            {quickLoading && <TableLoading>Loading swaps…</TableLoading>}
            <RecentSwapsTable style={{ minWidth: 900 }}>
              <thead>
                <tr>
                  <th>Swap</th>
                  <th>Total value</th>
                  <th>Input amount</th>
                  <th>Output amount</th>
                  <th>Account</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {combinedSwaps.length ? (
                  combinedSwaps.map((swap) => (
                    <tr key={swap.id}>
                      <td>
                        <ExplorerLink
                          href={`${TRANSACTION_EXPLORER_URL}${swap.transaction}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {swap.input} → {swap.output}
                        </ExplorerLink>
                      </td>
                      <td>{formatSwapUsd(swap.value)}</td>
                      <td>{swap.input}</td>
                      <td>{swap.output}</td>
                      <td>
                        <ExplorerLink href={`${ADDRESS_EXPLORER_URL}${swap.account}`} target="_blank" rel="noreferrer">
                          {shortAddress(swap.account)}
                        </ExplorerLink>
                      </td>
                      <td>{formatRelativeTime(swap.timestamp)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <EmptyRow colSpan={6}>
                      {selectedLoading ? 'Loading swaps...' : 'No indexed V2 or V3 swaps yet'}
                    </EmptyRow>
                  </tr>
                )}
              </tbody>
            </RecentSwapsTable>
          </StableTableCard>
          <Pagination aria-label="Combined recent swaps pages">
            {Array.from({ length: SWAP_PAGE_COUNT }, (_, page) => (
              <PageButton
                key={page}
                type="button"
                $active={combinedPage === page}
                disabled={selectedLoading || (page > combinedPage && !combinedHasNextPage)}
                onClick={() => selectPage(setCombinedPage, page)}
                aria-current={combinedPage === page ? 'page' : undefined}
              >
                {page + 1}
              </PageButton>
            ))}
          </Pagination>
        </Section>
      )}

      {view === 'v3' && (
        <Section style={{ order: 2 }}>
          <SectionTitle>Recent swaps</SectionTitle>
          <StableTableCard $minHeight={760}>
            {quickLoading && <TableLoading>Loading swaps…</TableLoading>}
            <RecentSwapsTable style={{ minWidth: 900 }}>
              <thead>
                <tr>
                  <th>Swap</th>
                  <th>Total value</th>
                  <th>Input amount</th>
                  <th>Output amount</th>
                  <th>Account</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {data?.swaps.length ? (
                  data.swaps.map((swap) => {
                    const { inputAmount, inputToken, outputAmount, outputToken } = getSwapAmounts(swap)
                    return (
                      <tr key={swap.id}>
                        <td>
                          <ExplorerLink
                            href={`${TRANSACTION_EXPLORER_URL}${swap.transaction.id}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {formatTokenQuantity(inputAmount)} {tokenLabel(inputToken)} →{' '}
                            {formatTokenQuantity(outputAmount)} {tokenLabel(outputToken)}
                          </ExplorerLink>
                        </td>
                        <td>{formatSwapUsd(getSwapUsdValue(swap, wonePriceUSD))}</td>
                        <td>
                          {formatTokenQuantity(inputAmount)} {tokenLabel(inputToken)}
                        </td>
                        <td>
                          {formatTokenQuantity(outputAmount)} {tokenLabel(outputToken)}
                        </td>
                        <td>
                          <ExplorerLink href={`${ADDRESS_EXPLORER_URL}${swap.origin}`} target="_blank" rel="noreferrer">
                            {shortAddress(swap.origin)}
                          </ExplorerLink>
                        </td>
                        <td>{formatRelativeTime(swap.timestamp)}</td>
                      </tr>
                    )
                  })
                ) : (
                  <tr>
                    <EmptyRow colSpan={6}>{loading ? 'Loading swaps...' : 'No indexed V3 swaps yet'}</EmptyRow>
                  </tr>
                )}
              </tbody>
            </RecentSwapsTable>
          </StableTableCard>
          <Pagination aria-label="V3 recent swaps pages">
            {Array.from({ length: SWAP_PAGE_COUNT }, (_, page) => (
              <PageButton
                key={page}
                type="button"
                $active={v3Page === page}
                disabled={loading || (page > v3Page && !v3HasNextPage)}
                onClick={() => selectPage(setV3Page, page)}
                aria-current={v3Page === page ? 'page' : undefined}
              >
                {page + 1}
              </PageButton>
            ))}
          </Pagination>
        </Section>
      )}

      {view === 'v2' && (
        <Section style={{ order: 2 }}>
          <SectionTitle>Recent V2 swaps</SectionTitle>
          <StableTableCard $minHeight={760}>
            {quickLoading && <TableLoading>Loading swaps…</TableLoading>}
            <RecentSwapsTable style={{ minWidth: 850 }}>
              <thead>
                <tr>
                  <th>Swap</th>
                  <th>Total value</th>
                  <th>Input amount</th>
                  <th>Output amount</th>
                  <th>Account</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {v2Swaps.length ? (
                  v2Swaps.map(({ transaction, ...swap }, index) => {
                    const token0IsInput = Number(swap.amount0In) > 0
                    const inputAmount = Number(token0IsInput ? swap.amount0In : swap.amount1In)
                    const outputAmount = Number(token0IsInput ? swap.amount1Out : swap.amount0Out)
                    const inputToken = token0IsInput ? swap.pair.token0.symbol : swap.pair.token1.symbol
                    const outputToken = token0IsInput ? swap.pair.token1.symbol : swap.pair.token0.symbol
                    return (
                      <tr key={`${transaction.id}-${index}`}>
                        <td>
                          <ExplorerLink
                            href={`${TRANSACTION_EXPLORER_URL}${transaction.id}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {formatTokenQuantity(inputAmount)} {inputToken || 'token'} →{' '}
                            {formatTokenQuantity(outputAmount)} {outputToken || 'token'}
                          </ExplorerLink>
                        </td>
                        <td>{formatSwapUsd(getV2SwapUsdValue(swap, v2WcroPriceUSD))}</td>
                        <td>
                          {formatTokenQuantity(inputAmount)} {inputToken}
                        </td>
                        <td>
                          {formatTokenQuantity(outputAmount)} {outputToken}
                        </td>
                        <td>
                          <ExplorerLink href={`${ADDRESS_EXPLORER_URL}${swap.to}`} target="_blank" rel="noreferrer">
                            {shortAddress(swap.to)}
                          </ExplorerLink>
                        </td>
                        <td>{formatRelativeTime(transaction.timestamp)}</td>
                      </tr>
                    )
                  })
                ) : (
                  <tr>
                    <EmptyRow colSpan={6}>{v2Loading ? 'Loading swaps...' : 'No indexed V2 swaps yet'}</EmptyRow>
                  </tr>
                )}
              </tbody>
            </RecentSwapsTable>
          </StableTableCard>
          <Pagination aria-label="V2 recent swaps pages">
            {Array.from({ length: SWAP_PAGE_COUNT }, (_, page) => (
              <PageButton
                key={page}
                type="button"
                $active={v2Page === page}
                disabled={v2Loading || (page > v2Page && !v2HasNextPage)}
                onClick={() => selectPage(setV2Page, page)}
                aria-current={v2Page === page ? 'page' : undefined}
              >
                {page + 1}
              </PageButton>
            ))}
          </Pagination>
        </Section>
      )}

      {view !== 'combined' && (
        <SearchInput
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search pools, tokens, or addresses"
          aria-label="Search analytics"
        />
      )}

      {view === 'v3' && (
        <Section>
          <SectionTitle>Top pools by TVL</SectionTitle>
          <StableTableCard $minHeight={520}>
            {quickLoading && <TableLoading>Loading pools…</TableLoading>}
            <Table>
              <thead>
                <tr>
                  <th>Pool</th>
                  <th>Fee tier</th>
                  <th>TVL</th>
                  <th>Total volume</th>
                  <th>Total fees</th>
                  <th>Transactions</th>
                </tr>
              </thead>
              <tbody>
                {pools.length ? (
                  paged(pools, v3PoolsPage).map((pool) => (
                    <tr key={pool.id}>
                      <td>
                        <PoolLink to={`/analytics/v3/${pool.id}`}>
                          {tokenLabel(pool.token0)} / {tokenLabel(pool.token1)}
                        </PoolLink>
                        <ReserveDetail>
                          {formatTokenQuantity(pool.totalValueLockedToken0)} {tokenLabel(pool.token0)} +{' '}
                          {formatTokenQuantity(pool.totalValueLockedToken1)} {tokenLabel(pool.token1)}
                        </ReserveDetail>
                        <Address>{shortAddress(pool.id)}</Address>
                      </td>
                      <td>{formatFeeTier(pool.feeTier)}</td>
                      <td>{formatNumber(pool.totalValueLockedUSD, '$')}</td>
                      <td>{formatNumber(getPoolVolumeUsd(pool, wonePriceUSD), '$')}</td>
                      <td>{formatNumber(getPoolFeesUsd(pool, wonePriceUSD), '$')}</td>
                      <td>{formatNumber(pool.txCount)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <EmptyRow colSpan={6}>
                      {loading ? 'Loading pools...' : search ? 'No pools match this search' : 'No indexed V3 pools yet'}
                    </EmptyRow>
                  </tr>
                )}
              </tbody>
            </Table>
          </StableTableCard>
          <Pagination aria-label="V3 top pools pages">
            {Array.from({ length: Math.max(1, Math.ceil(pools.length / TOP_LIST_PER_PAGE)) }, (_, page) => (
              <PageButton
                key={page}
                type="button"
                $active={v3PoolsPage === page}
                onClick={() => selectPage(setV3PoolsPage, page)}
              >
                {page + 1}
              </PageButton>
            ))}
          </Pagination>
        </Section>
      )}

      {view === 'v3' && (
        <Section>
          <SectionTitle>Top tokens by TVL</SectionTitle>
          <StableTableCard $minHeight={520}>
            {quickLoading && <TableLoading>Loading tokens…</TableLoading>}
            <Table>
              <thead>
                <tr>
                  <th>Token</th>
                  <th>Price</th>
                  <th>Token liquidity</th>
                  <th>TVL</th>
                  <th>Total volume</th>
                  <th>Total fees</th>
                  <th>Transactions</th>
                </tr>
              </thead>
              <tbody>
                {tokens.length ? (
                  paged(tokens, v3TokensPage).map((token) => (
                    <tr key={token.id}>
                      <td>
                        <PoolLink to={`/analytics/v3/token/${token.id}`}>{tokenLabel(token)}</PoolLink>
                        <Address>{shortAddress(token.id)}</Address>
                      </td>
                      <td>{formatUsdPrice(Number(token.derivedETH) * wonePriceUSD)}</td>
                      <td>{formatTokenQuantity(token.totalValueLocked)}</td>
                      <td>{formatNumber(token.totalValueLockedUSD, '$')}</td>
                      <td>{formatNumber(token.volumeUSD, '$')}</td>
                      <td>{formatNumber(token.feesUSD, '$')}</td>
                      <td>{formatNumber(token.txCount)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <EmptyRow colSpan={7}>
                      {loading
                        ? 'Loading tokens...'
                        : search
                        ? 'No tokens match this search'
                        : 'No indexed V3 tokens yet'}
                    </EmptyRow>
                  </tr>
                )}
              </tbody>
            </Table>
          </StableTableCard>
          <Pagination aria-label="V3 top tokens pages">
            {Array.from({ length: Math.max(1, Math.ceil(tokens.length / TOP_LIST_PER_PAGE)) }, (_, page) => (
              <PageButton
                key={page}
                type="button"
                $active={v3TokensPage === page}
                onClick={() => selectPage(setV3TokensPage, page)}
              >
                {page + 1}
              </PageButton>
            ))}
          </Pagination>
        </Section>
      )}

      {view === 'v2' && (
        <>
          <Section>
            <SectionTitle>Top V2 pairs by liquidity</SectionTitle>
            <StableTableCard $minHeight={520}>
              {quickLoading && <TableLoading>Loading pairs…</TableLoading>}
              <Table>
                <thead>
                  <tr>
                    <th>Pair</th>
                    <th>Liquidity</th>
                    <th>Total volume</th>
                    <th>Transactions</th>
                  </tr>
                </thead>
                <tbody>
                  {v2Pairs.length ? (
                    paged(v2Pairs, v2PairsPage).map((pair) => (
                      <tr key={pair.id}>
                        <td>
                          <PoolLink to={`/analytics/v2/${pair.id}`}>
                            {tokenLabel(pair.token0)} / {tokenLabel(pair.token1)}
                          </PoolLink>
                          <ReserveDetail>
                            {formatTokenQuantity(pair.reserve0)} {tokenLabel(pair.token0)} +{' '}
                            {formatTokenQuantity(pair.reserve1)} {tokenLabel(pair.token1)}
                          </ReserveDetail>
                          <Address>{shortAddress(pair.id)}</Address>
                        </td>
                        <td>{formatNumber(Number(pair.reserveUSD), '$')}</td>
                        <td>{formatNumber(pair.volumeUSD, '$')}</td>
                        <td>{formatNumber(pair.txCount)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <EmptyRow colSpan={4}>
                        {v2Loading ? 'Loading pairs...' : search ? 'No pairs match this search' : 'No indexed V2 pairs'}
                      </EmptyRow>
                    </tr>
                  )}
                </tbody>
              </Table>
            </StableTableCard>
            <Pagination aria-label="V2 top pairs pages">
              {Array.from({ length: Math.max(1, Math.ceil(v2Pairs.length / TOP_LIST_PER_PAGE)) }, (_, page) => (
                <PageButton
                  key={page}
                  type="button"
                  $active={v2PairsPage === page}
                  onClick={() => selectPage(setV2PairsPage, page)}
                >
                  {page + 1}
                </PageButton>
              ))}
            </Pagination>
          </Section>

          <Section>
            <SectionTitle>Top V2 tokens by volume</SectionTitle>
            <StableTableCard $minHeight={520}>
              {quickLoading && <TableLoading>Loading tokens…</TableLoading>}
              <Table>
                <thead>
                  <tr>
                    <th>Token</th>
                    <th>Price</th>
                    <th>Token liquidity</th>
                    <th>Liquidity value</th>
                    <th>Total volume</th>
                    <th>Transactions</th>
                  </tr>
                </thead>
                <tbody>
                  {v2Tokens.length ? (
                    paged(v2Tokens, v2TokensPage).map((token) => {
                      const priceUSD = Number(token.derivedETH) * v2WcroPriceUSD
                      return (
                        <tr key={token.id}>
                          <td>
                            <PoolLink to={`/analytics/v2/token/${token.id}`}>{tokenLabel(token)}</PoolLink>
                            <Address>{shortAddress(token.id)}</Address>
                          </td>
                          <td>{formatUsdPrice(priceUSD)}</td>
                          <td>{formatTokenQuantity(token.totalLiquidity)}</td>
                          <td>{formatNumber(Number(token.totalLiquidity) * priceUSD, '$')}</td>
                          <td>{formatNumber(token.tradeVolumeUSD, '$')}</td>
                          <td>{formatNumber(token.txCount)}</td>
                        </tr>
                      )
                    })
                  ) : (
                    <tr>
                      <EmptyRow colSpan={6}>
                        {v2Loading
                          ? 'Loading tokens...'
                          : search
                          ? 'No tokens match this search'
                          : 'No indexed V2 tokens'}
                      </EmptyRow>
                    </tr>
                  )}
                </tbody>
              </Table>
            </StableTableCard>
            <Pagination aria-label="V2 top tokens pages">
              {Array.from({ length: Math.max(1, Math.ceil(v2Tokens.length / TOP_LIST_PER_PAGE)) }, (_, page) => (
                <PageButton
                  key={page}
                  type="button"
                  $active={v2TokensPage === page}
                  onClick={() => selectPage(setV2TokensPage, page)}
                >
                  {page + 1}
                </PageButton>
              ))}
            </Pagination>
          </Section>
        </>
      )}
    </Page>
  )
}
